/**
 * Date Utility Functions
 * Provides consistent date formatting across the HMS application
 */

// Timezone mapping object to convert from full timezone to abbreviation
const timezoneAbbreviations = {
  'Europe/Berlin': 'CET',      // Central European Time
  'Europe/London': 'GMT',      // Greenwich Mean Time
  'America/New_York': 'EST',   // Eastern Standard Time
  'America/Chicago': 'CST',    // Central Standard Time
  'America/Los_Angeles': 'PST', // Pacific Standard Time
  'America/Sao_Paulo': 'BRT',  // Brasilia Time
  'Africa/Johannesburg': 'SAST', // South Africa Standard Time
  'Asia/Riyadh': 'AST',        // Arabia Standard Time
  'Asia/Tokyo': 'JST',         // Japan Standard Time
  'Asia/Singapore': 'SGT',     // Singapore Time
  'Australia/Sydney': 'AEST',  // Australian Eastern Standard Time
  'Europe/Warsaw': 'CET',      // Central European Time (same as Berlin)
  'Europe/Moscow': 'MSK'       // Moscow Standard Time
};

/**
 * Convert timezone string to abbreviation
 * @param {string} timezone - Full timezone string (e.g. 'Europe/Berlin')
 * @returns {string} Timezone abbreviation (e.g. 'CET')
 */
export const getTimezoneAbbreviation = (timezone) => {
  return timezoneAbbreviations[timezone] || timezone;
};

/**
 * Formats a date and time for display in the deals table
 * @param {string} dateStr - Date string in DD/MM/YYYY format
 * @param {string} timeStr - Time string in HH:MM 24-hour format
 * @returns {string} Formatted date and time in DD/MM/YYYY H:MM:SS AM/PM format
 */
export const formatValidityDisplayDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return 'N/A';
  
  try {
    // Parse date parts (assuming DD/MM/YYYY format)
    const [day, month, year] = dateStr.split('/').map(Number);
    
    // Parse time parts (assuming HH:MM 24-hour format)
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    // Create date object
    const date = new Date(year, month - 1, day, hours, minutes);
    
    // Format date as DD/MM/YYYY
    const formattedDate = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    
    // Format time with seconds in 12-hour format with AM/PM
    let hours12 = hours % 12;
    if (hours12 === 0) hours12 = 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedTime = `${hours12}:${minutes.toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')} ${ampm}`;
    
    // Return the combined formatted date and time
    return `${formattedDate} ${formattedTime}`;
  } catch (error) {
    console.error('Error formatting display date time:', error);
    return 'N/A';
  }
};

/**
 * Check if a validity date is in the past
 * @param {string} dateStr - Date string in DD/MM/YYYY format
 * @param {string} timeStr - Time string in HH:MM 24-hour format
 * @returns {boolean} True if the date is in the past, false otherwise
 */
export const isValidityExpired = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return false;
  
  try {
    // Parse date parts (assuming DD/MM/YYYY format)
    const [day, month, year] = dateStr.split('/').map(Number);
    
    // Parse time parts (assuming HH:MM 24-hour format)
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    // Create date object for validity date
    const validityDate = new Date(year, month - 1, day, hours, minutes);
    
    // Get current date and time
    const currentDate = new Date();
    
    // Compare dates
    return validityDate < currentDate;
  } catch (error) {
    console.error('Error checking if validity date is expired:', error);
    return false;
  }
};

/**
 * Formats date, time and timezone for validity sections in a consistent format
 * @param {string} dateStr - Date string in DD/MM/YYYY format
 * @param {string} timeStr - Time string in HH:MM 24-hour format
 * @param {string} timezone - Timezone string
 * @returns {string} Formatted validity text
 */
export const formatValidityDateTime = (dateStr, timeStr, timezone) => {
  if (!dateStr || !timeStr) return 'N/A';
  
  try {
    // Parse date parts (assuming DD/MM/YYYY format)
    const [day, month, year] = dateStr.split('/').map(Number);
    
    // Parse time parts (assuming HH:MM 24-hour format)
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    // Create date object
    const date = new Date(year, month - 1, day, hours, minutes);
    
    // Get ordinal suffix for day
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    // Format day with ordinal suffix
    const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
    
    // Get month name
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = monthNames[month - 1];
    
    // Format time in 12-hour with am/pm
    let hours12 = hours % 12;
    if (hours12 === 0) hours12 = 12;
    const ampm = hours >= 12 ? 'pm' : 'am';
    const formattedTime = `${hours12}${minutes > 0 ? ':' + minutes.toString().padStart(2, '0') : ''}${ampm}`;
    
    // Convert timezone to abbreviation if available
    const timezoneAbbr = getTimezoneAbbreviation(timezone);
    
    // Combine all parts
    return `This offer is valid until ${dayWithSuffix} ${monthName} ${year} ${formattedTime} ${timezoneAbbr} and subject to Seller's reconfirmation.`;
  } catch (error) {
    console.error('Error formatting validity date:', error);
    return 'This offer is valid until the specified date and time and subject to Seller\'s reconfirmation.';
  }
};

/**
 * Format a birthday date to display as "1 May" or "17 September"
 * Only displays day and month, year-agnostic
 * 
 * @param {string|Date} dateValue - Date string or Date object
 * @returns {string} Formatted birthday string (e.g., "1 May", "17 September")
 */
export const formatBirthday = (dateValue) => {
  if (!dateValue) return 'Not set';
  
  try {
    const date = new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Not set';
    }
    
    // Get day (without leading zero)
    const day = date.getDate();
    
    // Get full month name
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = monthNames[date.getMonth()];
    
    return `${day} ${month}`;
  } catch (error) {
    console.error('Error formatting birthday:', error);
    return 'Not set';
  }
};

/**
 * Format a birthday for input fields (DD/MM format)
 * Used in date pickers where year is not relevant
 * 
 * @param {Date} date - JavaScript Date object
 * @returns {string} Formatted string in DD/MM format
 */
export const formatBirthdayInput = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  
  return `${day}/${month}`;
};